import { XYCoord } from 'dnd-core';
import React from 'react';
import {
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DropTarget,
  DropTargetConnector,
  DropTargetMonitor,
} from 'react-dnd';
import { Tooltip } from '../../../../components';
import { MenuOption } from '../../types';
const classNames = require('classnames');

interface CarouselEditorCellBaseProps {
  hideMenu: (option: MenuOption) => void;
  openMenu: (option: MenuOption) => void;
  onToggle: (option: MenuOption) => void;
  onDelete: (option: MenuOption) => void;
  onEdit: (option: MenuOption) => void;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;

  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  option: MenuOption;
  menuOpen: boolean;
  isPriceEditable: boolean;
}

interface CarouselEditorCellProps extends CarouselEditorCellBaseProps {
  innerRef: any;
}

class CarouselEditorCellUnwrapped extends React.Component<CarouselEditorCellProps> {
  hideMenu = () => {
    this.props.hideMenu(this.props.option);
  };

  showMenu = () => {
    this.props.openMenu(this.props.option);
  };

  toggleEnabled = () => {
    this.props.onToggle(this.props.option);
  };

  delete = () => {
    this.props.onDelete(this.props.option);
  };

  onClick = () => {
    if (this.props.isPriceEditable) {
      this.props.onEdit(this.props.option);
    }
  };

  render() {
    const { option, connectDragSource, connectDropTarget } = this.props;

    let content;

    if (option.option.imageUrl) {
      content = <img src={option.option.imageUrl} className='carousel-editor-cell-img' />;
    } else {
      content = <p className='carousel-editor-cell-center-title'>{option.option.name}</p>;
    }

    connectDragSource(this.props.innerRef);
    connectDropTarget(this.props.innerRef);

    const className = classNames('carousel-editor-cell', {
      disabled: !option.enabled,
    });

    return (
      <div ref={this.props.innerRef} onClick={this.onClick} className={className}>
        <Tooltip hideMenu={this.hideMenu} openMenu={this.showMenu} open={this.props.menuOpen}>
          <Tooltip.Action onClick={this.toggleEnabled}>{!option.enabled ? 'Enable' : 'Disable'}</Tooltip.Action>
          <Tooltip.Action onClick={this.delete}>Delete</Tooltip.Action>
        </Tooltip>
        <div className='carousel-editor-cell-content'>{content}</div>
        <p className='carousel-editor-cell-price'>+${option.price}</p>
      </div>
    );
  }
}

const CarouselEditorCellWithRef = React.forwardRef((props: CarouselEditorCellBaseProps, ref) => (
  <CarouselEditorCellUnwrapped {...props} innerRef={ref} />
));

// export const MenuTile = dragTarget(dragSource(MenuTileUnwrapped));
export const CarouselEditorCell = DropTarget(
  'CarouselEditorCell',
  {
    hover(props: CarouselEditorCellBaseProps, monitor: DropTargetMonitor, node: HTMLDivElement | null) {
      if (!node) {
        return null;
      }

      const dragIndex = monitor.getItem().index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = node.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      // Time to actually perform the action
      props.moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      monitor.getItem().index = hoverIndex;
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    'CarouselEditorCell',
    {
      beginDrag: (props: CarouselEditorCellBaseProps) => ({
        index: props.index,
      }),
    },
    (connect: DragSourceConnector) => ({
      connectDragSource: connect.dragSource(),
    })
  )(CarouselEditorCellWithRef)
);
